import React, { useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress, ListItemIcon, Breadcrumbs, TextField } from '@mui/material';
import BrushIcon from '@mui/icons-material/Brush';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { backendUrl } from '../config';

const CANVAS_W = 800;
const CANVAS_H = 600;

// --- API helpers (compatibles explorerApp/NotepadApp) ---
import api from '../services/api';

/**
 * Récupère les fichiers de l'explorateur pour le chemin spécifié
 * @param {string} path - Chemin du répertoire à lister
 * @param {Function} setExplorerFiles - Fonction pour mettre à jour la liste des fichiers
 * @param {Function} setExplorerError - Fonction pour définir une erreur
 * @param {Function} setExplorerLoading - Fonction pour définir l'état de chargement
 * @returns {Promise<Array>} Liste des fichiers et dossiers
 */
async function fetchExplorerFiles(path = '/', setExplorerFiles, setExplorerError, setExplorerLoading) {
  // Définir l'état de chargement
  if (setExplorerLoading) setExplorerLoading(true);
  if (setExplorerError) setExplorerError('');
  
  try {
    // Récupérer les informations de l'utilisateur
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    
    // Essayer d'extraire l'identifiant utilisateur
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        uuid7 = userData.uuid7 || userData.uuid || userData.id;
      } catch (e) {
        console.error('Erreur lors de l\'analyse des données utilisateur:', e);
      }
    }
    
    if (!uuid7) {
      const errorMsg = 'Identifiant utilisateur non trouvé';
      if (setExplorerError) setExplorerError(errorMsg);
      throw new Error(errorMsg);
    }
    
    // Récupérer le token d'authentification
    const token = window.localStorage.getItem('token');
    if (!token) {
      const errorMsg = 'Token d\'authentification manquant';
      if (setExplorerError) setExplorerError(errorMsg);
      throw new Error(errorMsg);
    }
    
    // Nettoyer le chemin
    const relPath = path === '/' ? '' : path.replace(/^\/+/, '');
    
    // Effectuer la requête API
    const res = await api.get(
      `/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    // Mettre à jour l'état avec les données reçues
    const files = Array.isArray(res?.data) ? res.data : [];
    if (setExplorerFiles) setExplorerFiles(files);
    return files;
    
  } catch (error) {
    // Gestion des erreurs
    const errorMsg = error.response?.data?.message || 
                    error.message || 
                    'Erreur lors du chargement des fichiers';
                    
    console.error('Erreur fetchExplorerFiles:', error);
    
    if (setExplorerError) setExplorerError(errorMsg);
    if (setExplorerFiles) setExplorerFiles([]);
    
    throw error;
    
  } finally {
    // Toujours désactiver l'état de chargement
    if (setExplorerLoading) setExplorerLoading(false);
  }
}

async function downloadImageFromBackend(explorerPath, fileName) {
  // Télécharge une image sauvegardée via /save-file (dataURL ou base64)
  const userStr = window.localStorage.getItem('user');
  let uuid7 = null;
  if (userStr) {
    try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
  }
  if (!uuid7) throw new Error('Utilisateur non trouvé');
  const token = window.localStorage.getItem('token');
  let relPath = explorerPath === '/' ? fileName : explorerPath.replace(/^\//, '') + '/' + fileName;
  const res = await api.get(`/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  let content = res.data.content;
  // Si le fichier contient déjà un dataURL complet, on le retourne tel quel
  if (typeof content === 'string' && content.trim().startsWith('data:')) {
    return content;
  }
  // Sinon, deviner le type à partir de l'extension
  let ext = fileName.split('.').pop().toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') {
    return 'data:image/jpeg;base64,' + content;
  } else if (ext === 'svg') {
    // SVG peut être du texte ou du base64
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(content.trim())) {
      return 'data:image/svg+xml;base64,' + content;
    } else {
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(content);
    }
  } else {
    // Par défaut PNG
    return 'data:image/png;base64,' + content;
  }
}

// Sauvegarde une image dans le backend via /upload-file (binaire natif)
async function saveImageToBackend(path, dataUrl) {
  const userStr = window.localStorage.getItem('user');
  let uuid7 = null;
  if (userStr) {
    try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
  }
  if (!uuid7) throw new Error('Utilisateur non trouvé');
  const token = window.localStorage.getItem('token');
  let relPath = path === '/' ? '' : path.replace(/^\//, '');
  // Convertit le dataUrl en Blob
  function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  }
  const blob = dataURLtoBlob(dataUrl);
  const formData = new FormData();
  formData.append('uuid7', uuid7);
  formData.append('path', relPath);
  formData.append('file', blob, relPath.split('/').pop());
  const res = await fetch(`${backendUrl}/upload-file`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData
  });
  if (!res.ok) throw new Error('Erreur lors de la sauvegarde du fichier');
  return await res.json();
}



export default function PaintApp() {
  // --- Chargement auto d’une image depuis FileExplorer ---
  React.useEffect(() => {
    const blobUrl = window.localStorage.getItem('fileExplorer_fileBlobUrl');
    const filePath = window.localStorage.getItem('fileExplorer_filePath');
    if (blobUrl && filePath && canvasRef.current) {
      const img = new window.Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        // Respecte le ratio d'origine de l'image
        const imgRatio = img.width / img.height;
        const canvasRatio = CANVAS_W / CANVAS_H;
        let drawWidth, drawHeight, offsetX, offsetY;
        if (imgRatio > canvasRatio) {
          drawWidth = CANVAS_W;
          drawHeight = CANVAS_W / imgRatio;
          offsetX = 0;
          offsetY = (CANVAS_H - drawHeight) / 2;
        } else {
          drawHeight = CANVAS_H;
          drawWidth = CANVAS_H * imgRatio;
          offsetX = (CANVAS_W - drawWidth) / 2;
          offsetY = 0;
        }
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        saveToHistory && saveToHistory();
      };
      img.src = blobUrl;
      // Nettoyage pour éviter un doublon
      window.localStorage.removeItem('fileExplorer_fileBlobUrl');
      window.localStorage.removeItem('fileExplorer_filePath');
    }
  }, []);

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [tool, setTool] = useState('brush');
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [color, setColor] = useState('#222222');
  const [size, setSize] = useState(4);

  // Start drawing
  const handlePointerDown = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(
      e.nativeEvent.offsetX,
      e.nativeEvent.offsetY
    );
    setDrawing(true);
  };

  // Drawing
  const handlePointerMove = (e) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(
      e.nativeEvent.offsetX,
      e.nativeEvent.offsetY
    );
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  // Stop drawing
  const handlePointerUp = () => {
    if (drawing) {
      setDrawing(false);
      saveToHistory();
    }
  };

  // Save canvas state for undo
  const saveToHistory = () => {
    const canvas = canvasRef.current;
    setHistory((h) => [...h, canvas.toDataURL()]);
    setRedoStack([]);
  };

  // Undo
  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = [...history];
    const last = prev.pop();
    setRedoStack((r) => [canvasRef.current.toDataURL(), ...r]);
    setHistory(prev);
    const img = new window.Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.drawImage(img, 0, 0);
    };
    img.src = prev.length > 0 ? prev[prev.length - 1] : '';
  };
  // Redo
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const [imgData, ...rest] = redoStack;
    setRedoStack(rest);
    setHistory((h) => [...h, imgData]);
    const img = new window.Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.drawImage(img, 0, 0);
    };
    img.src = imgData;
  };

  // Open local image
  const handleOpenFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new window.Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        // Respecte le ratio d'origine de l'image
const imgRatio = img.width / img.height;
const canvasRatio = CANVAS_W / CANVAS_H;
let drawWidth, drawHeight, offsetX, offsetY;
if (imgRatio > canvasRatio) {
  // Image plus large que le canvas
  drawWidth = CANVAS_W;
  drawHeight = CANVAS_W / imgRatio;
  offsetX = 0;
  offsetY = (CANVAS_H - drawHeight) / 2;
} else {
  // Image plus haute que le canvas
  drawHeight = CANVAS_H;
  drawWidth = CANVAS_H * imgRatio;
  offsetX = (CANVAS_W - drawWidth) / 2;
  offsetY = 0;
}
ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        saveToHistory();
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Save as PNG
  const handleSave = () => {
    const url = canvasRef.current.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dessin.png';
    link.click();
  };

  // File explorer open
  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  // --- Explorer backend ---
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');
  // --- Save dialog backend ---
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savePath, setSavePath] = useState('/');
  const [saveName, setSaveName] = useState('dessin.png');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveExplorerFiles, setSaveExplorerFiles] = useState([]);

  // Explorer backend open
  const openExplorer = (path = '/') => {
    setExplorerPath(path);
    fetchExplorerFiles(path, setExplorerFiles, setExplorerError, setExplorerLoading);
  };
  // Ouvre une image backend (nouveau : récupère le blobUrl depuis localStorage)
  const handleExplorerSelectFile = async (file) => {
    if (file.type !== 'file') return;
    
    setExplorerLoading(true);
    setExplorerError(null);
    
    try {
      // Récupérer les informations de l'utilisateur
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      
      // Extraire l'identifiant utilisateur
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          uuid7 = userData.uuid7 || userData.uuid || userData.id;
        } catch (e) {
          console.error('Erreur lors de l\'analyse des données utilisateur:', e);
        }
      }
      
      if (!uuid7) {
        throw new Error('Identifiant utilisateur non trouvé');
      }
      
      // Récupérer le token d'authentification
      const token = window.localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }
      
      // Construire le chemin relatif de manière sécurisée
      const cleanPath = (path) => path.replace(/^\/+|\/+$/g, '');
      const relPath = explorerPath === '/' 
        ? file.name 
        : `${cleanPath(explorerPath)}/${file.name}`;
      
      // Construire l'URL complète avec le backendUrl
      const url = new URL(
        `${backendUrl}/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`,
        backendUrl
      ).toString();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('Erreur lors du téléchargement du fichier');
      const ext = file.name.split('.').pop().toLowerCase();
      if (["png","jpg","jpeg","gif","svg","bmp","webp"].includes(ext)) {
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const img = new window.Image();
        img.onload = () => {
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
          // Respecte le ratio d'origine de l'image
          const imgRatio = img.width / img.height;
          const canvasRatio = CANVAS_W / CANVAS_H;
          let drawWidth, drawHeight, offsetX, offsetY;
          if (imgRatio > canvasRatio) {
            drawWidth = CANVAS_W;
            drawHeight = CANVAS_W / imgRatio;
            offsetX = 0;
            offsetY = (CANVAS_H - drawHeight) / 2;
          } else {
            drawHeight = CANVAS_H;
            drawWidth = CANVAS_H * imgRatio;
            offsetX = (CANVAS_W - drawWidth) / 2;
            offsetY = 0;
          }
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          saveToHistory();
          setExplorerOpen(false);
          URL.revokeObjectURL(blobUrl);
        };
        img.src = blobUrl;
      } else {
        setExplorerError('Format de fichier non supporté pour l’affichage.');
      }
    } catch (e) {
      setExplorerError('Erreur lors de la lecture du fichier.');
    } finally {
      setExplorerLoading(false);
    }
  };
  // Naviguer dans explorer
  const handleExplorerNavigate = (folder) => {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath, setExplorerFiles, setExplorerError, setExplorerLoading);
  };
  const handleExplorerGoUp = () => {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath, setExplorerFiles, setExplorerError, setExplorerLoading);
  };
  // --- Save to backend ---
  const openSaveDialog = async (path = '/') => {
    setSaveLoading(true);
    setSaveError('');
    try {
      const files = await fetchExplorerFiles(path);
      setSaveExplorerFiles(files);
      setSavePath(path);
    } catch (e) {
      setSaveError('Erreur chargement explorer');
    } finally {
      setSaveLoading(false);
    }
  };
  const handleSaveDialogOpen = () => {
    openSaveDialog('/');
    setSaveDialogOpen(true);
  };
  const handleSaveDialogValidate = async () => {
    setSaveLoading(true);
    setSaveError('');
    try {
      // Detect extension
      let ext = saveName.split('.').pop().toLowerCase();
      let mime = 'image/png';
      if (ext === 'jpg' || ext === 'jpeg') mime = 'image/jpeg';
      else if (ext === 'svg') mime = 'image/svg+xml';
      // For SVG, we cannot use toDataURL, so get SVG source from canvas is not possible. Only raster formats are supported for canvas export.
      let dataUrl;
      if (mime === 'image/svg+xml') {
        setSaveError('L’export SVG natif n’est pas supporté depuis le canvas.');
        setSaveLoading(false);
        return;
      } else {
        dataUrl = canvasRef.current.toDataURL(mime);
      }
      const blob = await fetch(dataUrl).then(res => res.blob());
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      const relPath = savePath + (savePath.endsWith('/') ? '' : '/') + saveName;
      const formData = new FormData();
      formData.append('uuid7', uuid7);
      formData.append('path', relPath.replace(/^\//, ''));
      formData.append('file', blob, saveName);
      await fetch(`${backendUrl}/upload-file`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      setSaveDialogOpen(false);
    } catch (e) {
      setSaveError('Erreur sauvegarde image');
    } finally {
      setSaveLoading(false);
    }
  };
  const handleSaveExplorerNavigate = (folder) => {
    openSaveDialog(savePath + (savePath.endsWith('/') ? '' : '/') + folder);
  };
  const handleSaveExplorerGoUp = () => {
    if (savePath === '/') return;
    const parts = savePath.split('/').filter(Boolean);
    parts.pop();
    openSaveDialog(parts.length === 0 ? '/' : '/' + parts.join('/'));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: CANVAS_W + 40, mx: 'auto', mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Tooltip title="Ouvrir une image locale">
          <IconButton color="primary" onClick={openFileDialog}>
            <UploadFileIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ouvrir depuis l'explorateur">
          <IconButton color="primary" onClick={() => { setExplorerOpen(true); openExplorer('/'); }}>
            <FolderOpenIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Pinceau">
          <IconButton color={tool === 'brush' ? 'secondary' : 'default'} onClick={() => setTool('brush')}>
            <BrushIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Annuler">
          <IconButton onClick={handleUndo}>
            <UndoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rétablir">
          <IconButton onClick={handleRedo}>
            <RedoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sauvegarder (local)">
          <IconButton color="success" onClick={handleSave}>
            <SaveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Enregistrer sur l'explorateur (backend)">
          <IconButton color="info" onClick={handleSaveDialogOpen}>
            <SaveIcon />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,image/svg+xml"
          style={{ display: 'none' }}
          onChange={handleOpenFile}
        />
        <input
          type="color"
          value={color}
          onChange={e => setColor(e.target.value)}
          title="Couleur du pinceau"
          style={{ marginLeft: 16 }}
        />
        <input
          type="range"
          min="1"
          max="40"
          value={size}
          onChange={e => setSize(Number(e.target.value))}
          title="Taille du pinceau"
          style={{ marginLeft: 8 }}
        />
        <Typography sx={{ ml: 1 }}>Taille: {size}px</Typography>
      </Stack>
      <Box
        sx={{
          border: '2px solid #888',
          borderRadius: 2,
          background: '#fff',
          width: CANVAS_W,
          height: CANVAS_H,
          cursor: tool === 'brush' ? 'crosshair' : 'default',
          boxShadow: 3,
        }}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{ width: CANVAS_W, height: CANVAS_H, display: 'block', background: '#fff' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
      </Box>

      {/* --- Explorateur de fichiers backend --- */}
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ouvrir une image depuis l'explorateur</DialogTitle>
        <DialogContent>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 15, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => openExplorer('/')}>Racine</span>
              {explorerPath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => openExplorer(path)}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
          </Box>
          <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 270, overflowY: 'auto', mt: 1, mb: 2 }}>
            {explorerLoading ? <CircularProgress /> : (
              <List sx={{ fontSize: 17 }}>
                {explorerFiles.map((file, idx) => (
                  <ListItem
                    button={file.type === 'folder'}
                    key={idx}
                    onDoubleClick={file.type === 'folder' ? () => handleExplorerNavigate(file.name) : undefined}
                    onClick={file.type === 'file' ? () => handleExplorerSelectFile(file) : undefined}
                    sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: file.type === 'folder' ? '#e3f2fd' : undefined } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontSize: 17, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222222' }}>{file.name}</span>}
                      secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                      style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                    />
                  </ListItem>
                ))}
                {(!explorerFiles || explorerFiles.length === 0) && <ListItem><ListItemText primary={<span style={{ fontSize: 16 }}>Dossier vide</span>} /></ListItem>}
              </List>
            )}
            {explorerError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{explorerError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)} size="large">Annuler</Button>
        </DialogActions>
      </Dialog>

      {/* --- Dialog sauvegarde backend --- */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 20 }}>Enregistrer sur l'explorateur</DialogTitle>
        <DialogContent sx={{ bgcolor: '#f7f9fa', p: 3 }}>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="medium" onClick={handleSaveExplorerGoUp} disabled={savePath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 16, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => openSaveDialog('/')}>Racine</span>
              {savePath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => openSaveDialog(path)}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
          </Box>
          <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 270, overflowY: 'auto', mt: 1, mb: 2 }}>
            {saveLoading ? <CircularProgress /> : (
              <List sx={{ fontSize: 18 }}>
                {saveExplorerFiles.map((file, idx) => (
                  <ListItem
                    button={file.type === 'folder'}
                    key={idx}
                    onDoubleClick={file.type === 'folder' ? () => handleSaveExplorerNavigate(file.name) : undefined}
                    sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: file.type === 'folder' ? '#e3f2fd' : undefined } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontSize: 18, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222222' }}>{file.name}</span>}
                      secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                      style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                    />
                  </ListItem>
                ))}
                {(!saveExplorerFiles || saveExplorerFiles.length === 0) && <ListItem><ListItemText primary={<span style={{ fontSize: 17 }}>Dossier vide</span>} /></ListItem>}
              </List>
            )}
            {saveError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{saveError}</Typography>}
          </Box>
          <TextField label="Nom du fichier" value={saveName} onChange={e => setSaveName(e.target.value)} fullWidth size="medium" sx={{ fontSize: 18, mb: 1 }} InputLabelProps={{ style: { fontSize: 16 } }} InputProps={{ style: { fontSize: 18 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} size="large">Annuler</Button>
          <Button onClick={handleSaveDialogValidate} disabled={saveLoading} variant="contained" size="large">Valider</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}